import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';

import { Candidate } from '../../types';
import { db } from '../../firebase';

interface PrecinctVoterFilterDialogProps {
  open: boolean;
  onClose: () => void;
  onApplyFilter: (filters: {
    votedCandidate: string;
    qrDocumentStatus: 'generated' | 'not_generated' | 'all';
  }) => void;
  precinct: {
    precinct: string;
    barangay: string;
    city_municipality: string;
  };
}

const PrecinctVoterFilterDialog: React.FC<PrecinctVoterFilterDialogProps> = ({
  open,
  onClose,
  onApplyFilter,
  precinct
}) => {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [selectedCandidate, setSelectedCandidate] = useState<string>('');
  const [qrDocumentStatus, setQRDocumentStatus] = useState<'generated' | 'not_generated' | 'all'>('all');

  useEffect(() => {
    const fetchCandidates = async () => {
      try {
        const candidatesRef = collection(db, 'candidates');
        const candidatesSnapshot = await getDocs(candidatesRef);
        const fetchedCandidates = candidatesSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            type: data.type || 'provincial',
            color: data.color || '',
            name: data.name || '',
            position: data.position || ''
          } as Candidate;
        });
        setCandidates(fetchedCandidates);
      } catch (error) {
        console.error('Error fetching candidates:', error);
      }
    };

    if (open) {
      fetchCandidates();
    }
  }, [open]);

  const handleCandidateChange = (event: SelectChangeEvent) => {
    setSelectedCandidate(event.target.value as string);
  };

  const handleQRDocumentStatusChange = (event: SelectChangeEvent) => {
    setQRDocumentStatus(event.target.value as 'generated' | 'not_generated' | 'all');
  };

  const handleApplyFilter = () => {
    onApplyFilter({
      votedCandidate: selectedCandidate,
      qrDocumentStatus
    });
    onClose();
  };

  const handleClearFilter = () => {
    setSelectedCandidate('');
    setQRDocumentStatus('all');
    onApplyFilter({
      votedCandidate: '',
      qrDocumentStatus: 'all'
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Filter Precinct Voters</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Voted Candidate</InputLabel>
            <Select
              value={selectedCandidate}
              onChange={handleCandidateChange}
              label="Voted Candidate"
            >
              <MenuItem value="">All Candidates</MenuItem>
              {candidates.map((candidate) => (
                <MenuItem key={candidate.name} value={candidate.name}>
                  {candidate.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>QR Document Status</InputLabel>
            <Select
              value={qrDocumentStatus}
              label="QR Document Status"
              onChange={handleQRDocumentStatusChange}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="generated">Generated</MenuItem>
              <MenuItem value="not_generated">Not Generated</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClearFilter}>Clear Filters</Button>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleApplyFilter} variant="contained">Apply</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrecinctVoterFilterDialog;
